/**
 * PersistentChatPanel — right-side chat panel for Desktop ≥1280px.
 *
 * States:
 *   open      — full panel with messages + input (~380px wide)
 *   collapsed — narrow strip, click to expand
 *   pinned    — stays open across all canvas views (saved to localStorage)
 *
 * Supports two modes:
 *   Thread mode  — when `threads` prop is passed (T1c hybrid threading)
 *   Legacy mode  — when only `messages`/`onSend` are passed (fallback)
 *
 * See: docs/architecture/CHAT_ARCHITECTURE.md §3
 *      docs/architecture/UX_NAVIGATION_PATTERNS_V2.md §5
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';
import { useTranslation } from 'react-i18next';
import ChatMessages from '../components/ChatMessages';
import { saveChatState } from '../utils/viewMemory';

const CONTEXT_ICONS = {
  global: '🌍',
  field: '🌾',
  task: '✅',
  activity: '📋',
  enterprise: '🏢',
  betrieb: '🏡',
};

export default function PersistentChatPanel({
  // Thread mode props (T1c)
  threads,
  activeThreadId,
  activeMessages,
  sendingThread,
  onSendInThread,
  onSwitchThread,
  onPinThread,
  onArchiveThread,    // (threadId) → archive + switch to global (T1c-FIX)
  archiveToast,       // string | null — auto-dismiss toast from useThreads (T1c-FIX)
  // Legacy fallback props
  messages,
  sending: sendingLegacy,
  onSend,
  // Common props
  token,
  currentContext,
  initialPinned,
  initialCollapsed,
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [pinned, setPinned] = useState(initialPinned || false);
  const [collapsed, setCollapsed] = useState(initialCollapsed || false);
  const inputRef = useRef(null);

  // T1f: Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Determine which mode we're in
  const threadMode = !!(threads && onSendInThread);
  const displayMessages = threadMode ? (activeMessages || []) : (messages || []);
  const isSending = threadMode ? (sendingThread || false) : (sendingLegacy || false);

  // Save state to localStorage
  const handleSetPinned = (val) => {
    setPinned(val);
    saveChatState(val, collapsed);
  };
  const handleSetCollapsed = (val) => {
    setCollapsed(val);
    saveChatState(pinned, val);
  };

  const handleSend = useCallback(() => {
    if (!input.trim() || isSending) return;
    if (threadMode) {
      onSendInThread(input.trim());
    } else {
      onSend(input.trim());
    }
    setInput('');
    inputRef.current?.focus();
  }, [input, isSending, threadMode, onSendInThread, onSend]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // T1f: Debounced search
  useEffect(() => {
    if (!searchOpen || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(`/api/v1/aif/chat/search?q=${encodeURIComponent(searchQuery)}&limit=10`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.results || []);
        }
      } catch (_) { /* network error — silent */ }
      setSearchLoading(false);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery, searchOpen, token]);

  const handleSearchOpen = () => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const handleSearchClose = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSearchResultClick = (result) => {
    handleSearchClose();
    if (result.thread_id && onSwitchThread) {
      onSwitchThread(result.thread_id);
    }
  };

  // Context indicator label
  const VIEW_LABELS = { map: t('nav.map'), farm: t('nav.farm'), chat: t('nav.chat'), my_tasks: t('nav.myTasks'), reports: t('nav.reports') };
  const contextLabel = currentContext?.fieldName
    ? `${t('nav.map')} → ${currentContext.fieldName}`
    : currentContext?.taskId
      ? t('nav.myTasks')
      : currentContext?.view
        ? (VIEW_LABELS[currentContext.view] || currentContext.view)
        : currentContext?.tab
          ? (VIEW_LABELS[currentContext.tab] || currentContext.tab)
          : null;

  // ── Collapsed state ──────────────────────────────────────────────────────
  if (collapsed) {
    return (
      <aside
        className="w-[60px] flex-shrink-0 border-l border-[var(--aif-border-default)]
                   bg-[var(--aif-bg-surface)] flex flex-col items-center py-3 gap-3"
        data-testid="chat-panel-collapsed"
      >
        <button
          onClick={() => handleSetCollapsed(false)}
          className="w-9 h-9 flex items-center justify-center rounded-xl
                     bg-[var(--aif-bg-elevated)] text-[var(--aif-color-primary)]
                     hover:opacity-80 transition text-lg"
          title={t('chat.openPanel')}
          data-testid="chat-panel-expand"
        >
          💬
        </button>
        {displayMessages.length > 0 && (
          <span className="text-[10px] font-bold text-white bg-[var(--aif-color-primary)]
                           rounded-full w-5 h-5 flex items-center justify-center">
            {displayMessages.length > 9 ? '9+' : displayMessages.length}
          </span>
        )}
      </aside>
    );
  }

  // ── Active thread label for header ───────────────────────────────────────
  const activeThread = threads?.find(t => t.id === activeThreadId);
  const activeLabel = activeThread
    ? (activeThread.context_type === 'global' ? t('chat.threadGlobal') : activeThread.context_label)
    : contextLabel;

  return (
    <aside
      className="w-[380px] flex-shrink-0 border-l border-[var(--aif-border-default)]
                 bg-[var(--aif-bg-surface)] flex flex-col"
      data-testid="chat-panel"
    >
      {/* ── Panel Header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[var(--aif-text-primary)]">
            💬 AI Chat
          </span>
          {activeLabel && (
            <span className="text-[10px] text-[var(--aif-text-muted)] truncate max-w-[140px]">
              📍 {activeLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={handleSearchOpen}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-xs
                       text-[var(--aif-text-muted)] hover:bg-[var(--aif-bg-elevated)] transition"
            title={t('chat.searchTitle')}
            data-testid="chat-panel-search"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
          <button
            onClick={() => handleSetPinned(!pinned)}
            className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs transition
              ${pinned
                ? 'text-[var(--aif-color-primary)] bg-[var(--aif-color-primary-light,rgba(22,163,74,0.15))]'
                : 'text-[var(--aif-text-muted)] hover:bg-[var(--aif-bg-elevated)]'
              }`}
            title={pinned ? t('chat.unpin') : t('chat.pin')}
            data-testid="chat-panel-pin"
          >
            📌
          </button>
          <button
            onClick={() => handleSetCollapsed(true)}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-xs
                       text-[var(--aif-text-muted)] hover:bg-[var(--aif-bg-elevated)] transition"
            title={t('chat.minimize')}
            data-testid="chat-panel-collapse"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── Thread Tabs (scrollable with fixed pinned section) ────────────── */}
      {threadMode && threads && threads.length > 0 && (
        <ThreadTabBar
          threads={threads}
          activeThreadId={activeThreadId}
          onSwitchThread={onSwitchThread}
          onArchiveThread={onArchiveThread}
          t={t}
        />
      )}

      {/* ── T1f: Search overlay ─────────────────────────────────────────── */}
      {searchOpen && (
        <div className="flex flex-col border-b border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] flex-shrink-0 max-h-[60%] overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2">
            <svg className="w-4 h-4 text-[var(--aif-text-muted)] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') handleSearchClose(); }}
              placeholder={t('chat.searchPlaceholder')}
              className="flex-1 bg-transparent text-xs text-[var(--aif-text-primary)] outline-none placeholder-[var(--aif-text-muted)]"
              data-testid="chat-search-input"
            />
            <button
              onClick={handleSearchClose}
              className="w-6 h-6 flex items-center justify-center rounded text-[var(--aif-text-muted)] hover:bg-[var(--aif-bg-elevated)] text-xs"
            >
              ✕
            </button>
          </div>
          {searchLoading && (
            <div className="px-3 py-2 text-[10px] text-[var(--aif-text-muted)]">{t('chat.searching')}</div>
          )}
          {!searchLoading && searchResults.length > 0 && (
            <div className="overflow-y-auto flex-1">
              {searchResults.map((r, i) => (
                <button
                  key={r.message_id || i}
                  onClick={() => handleSearchResultClick(r)}
                  className="w-full text-left px-3 py-2 hover:bg-[var(--aif-bg-elevated)] transition border-t border-[var(--aif-border-default)]"
                  data-testid={`search-result-${i}`}
                >
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-[10px]">{CONTEXT_ICONS[r.context_type] || '💬'}</span>
                    <span className="text-[10px] font-medium text-[var(--aif-text-primary)] truncate">{r.thread_label}</span>
                    <span className="text-[9px] text-[var(--aif-text-muted)] ml-auto flex-shrink-0">
                      {r.message_date ? new Date(r.message_date).toLocaleDateString(undefined) : ''}
                    </span>
                  </div>
                  <p className="text-[10px] text-[var(--aif-text-secondary)] line-clamp-2 leading-relaxed">
                    {r.snippet}
                  </p>
                </button>
              ))}
            </div>
          )}
          {!searchLoading && searchQuery.length >= 2 && searchResults.length === 0 && (
            <div className="px-3 py-3 text-[10px] text-[var(--aif-text-muted)] text-center">{t('chat.noResults')}</div>
          )}
        </div>
      )}

      {/* ── Auto-archive toast ────────────────────────────────────────────── */}
      {archiveToast && (
        <div
          className="mx-2 mt-1.5 px-3 py-1.5 rounded-lg text-[11px] text-center flex-shrink-0
                     bg-[var(--aif-bg-elevated)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)]"
          data-testid="archive-toast"
        >
          {archiveToast}
        </div>
      )}

      {/* ── Messages ──────────────────────────────────────────────────────── */}
      <ChatMessages
        messages={displayMessages}
        sending={isSending}
        onSend={threadMode ? onSendInThread : onSend}
        compact
      />

      {/* ── Input ─────────────────────────────────────────────────────────── */}
      <div className="border-t border-[var(--aif-border-default)] px-3 py-2.5 flex-shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chat.placeholder', { defaultValue: 'Schreib eine Nachricht…' })}
            rows={1}
            className="flex-1 resize-none rounded-xl border border-[var(--aif-border-default)]
                       bg-[var(--aif-bg-input,var(--aif-bg-elevated))] px-3 py-2 text-xs
                       focus:outline-none focus:border-[var(--aif-border-focus)] transition-colors
                       text-[var(--aif-text-primary)]"
            style={{ maxHeight: '80px' }}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px';
            }}
            data-testid="chat-panel-input"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isSending}
            className="p-2 bg-[var(--aif-color-primary)] text-white rounded-xl transition
                       disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0
                       hover:opacity-90 active:scale-95"
            data-testid="chat-panel-send"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
}


/**
 * ThreadTabBar — scrollable tab bar with fixed pinned section.
 * Extracted from PersistentChatPanel for clarity.
 */
function ThreadTabBar({ threads, activeThreadId, onSwitchThread, onArchiveThread, t }) {
  const scrollRef = useRef(null);
  const activeRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const pinnedThreads = threads.filter(th => th.pinned || th.context_type === 'global');
  const unpinnedThreads = threads.filter(th => !th.pinned && th.context_type !== 'global');

  const updateScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScroll, { passive: true });
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateScroll);
      ro.observe(el);
    }
    return () => { el.removeEventListener('scroll', updateScroll); ro?.disconnect(); };
  }, [updateScroll, threads.length]);

  // Auto-scroll active tab into view
  useEffect(() => {
    const tab = activeRef.current;
    const container = scrollRef.current;
    if (!tab || !container) return;
    const tR = tab.getBoundingClientRect();
    const cR = container.getBoundingClientRect();
    if (tR.left < cR.left) container.scrollBy({ left: tR.left - cR.left - 8, behavior: 'smooth' });
    else if (tR.right > cR.right) container.scrollBy({ left: tR.right - cR.right + 8, behavior: 'smooth' });
  }, [activeThreadId]);

  const renderTab = (thread) => {
    const icon = CONTEXT_ICONS[thread.context_type] || '💬';
    const label = thread.context_type === 'global' ? t('chat.threadGlobal') : (thread.context_label || t('chat.thread.untitled', 'Chat'));
    const isActive = thread.id === activeThreadId;
    const isPinned = thread.pinned || thread.context_type === 'global';
    const isCloseable = !isPinned;

    return (
      <div
        key={thread.id}
        ref={isActive ? activeRef : undefined}
        className={`group flex items-center gap-0.5 rounded-lg flex-shrink-0 transition ${
          isActive
            ? 'bg-[var(--aif-color-primary)] text-white'
            : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-page)]'
        }`}
        data-testid={`thread-tab-${thread.context_type}`}
      >
        <button
          onClick={() => onSwitchThread(thread.id)}
          className="flex items-center gap-1 pl-2.5 py-1 text-[11px] font-medium whitespace-nowrap"
          title={label}
          data-testid={`thread-tab-label-${thread.id}`}
        >
          <span>{icon}</span>
          <span className="max-w-[100px] truncate" title={label}>{label}</span>
          {thread.pinned && thread.context_type !== 'global' && (
            <span className="opacity-60 text-[9px]">📌</span>
          )}
        </button>
        {isCloseable ? (
          <button
            onClick={(e) => { e.stopPropagation(); onArchiveThread?.(thread.id); }}
            className={`flex items-center justify-center w-6 h-6 rounded-r-lg flex-shrink-0 transition opacity-60 hover:opacity-100 ${
              isActive ? 'hover:bg-white/20' : 'hover:bg-[var(--aif-bg-elevated)]'
            }`}
            title={t('chat.closeThread', 'Tab schließen')}
            data-testid={`thread-tab-close-${thread.id}`}
            aria-label={`${label} ${t('chat.closeThread', 'Tab schließen')}`}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : (
          <span className="pr-2" />
        )}
      </div>
    );
  };

  const scrollArrow = (direction) => (
    <button
      type="button"
      onClick={() => scrollRef.current?.scrollBy({ left: direction === 'left' ? -120 : 120, behavior: 'smooth' })}
      className="absolute top-1/2 -translate-y-1/2 z-10 w-5 h-5 flex items-center justify-center bg-[var(--aif-bg-elevated)] rounded-full shadow-sm border border-[var(--aif-border-default)] hover:bg-[var(--aif-bg-tertiary)]"
      style={direction === 'left' ? { left: 0 } : { right: 0 }}
      aria-label={direction === 'left' ? t('chat.tabs.scrollLeft', 'Links scrollen') : t('chat.tabs.scrollRight', 'Rechts scrollen')}
    >
      <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d={direction === 'left' ? 'M15 18L9 12L15 6' : 'M9 6L15 12L9 18'} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );

  return (
    <div className="flex items-center gap-1 px-2 py-1.5 border-b border-[var(--aif-border-default)] bg-[var(--aif-bg-elevated)] flex-shrink-0" data-testid="thread-tabs">
      {pinnedThreads.length > 0 && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {pinnedThreads.map(renderTab)}
          {unpinnedThreads.length > 0 && (
            <div className="w-px h-5 bg-[var(--aif-border-default)] mx-0.5" aria-hidden="true" />
          )}
        </div>
      )}
      {unpinnedThreads.length > 0 && (
        <div className="relative flex-1 min-w-0">
          {canScrollLeft && scrollArrow('left')}
          <div
            ref={scrollRef}
            className="flex items-center gap-1 overflow-x-auto scrollbar-none"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {unpinnedThreads.map(renderTab)}
          </div>
          {canScrollRight && scrollArrow('right')}
        </div>
      )}
    </div>
  );
}
